import { useEffect, useRef, useState, useCallback } from 'react';
import { Plus, BookOpen, Trash2, FileText, UploadCloud, RefreshCw, Database } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { CardSkeletonGrid, EmptyState } from '../components/ui/States';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { Drawer } from '../components/ui/Drawer';
import { Field, Input, Select } from '../components/ui/Field';
import { IndexStatusBadge } from '../components/StatusBadges';
import { knowledgeBasesService } from '../services/knowledgeBasesService';
import { useModelsStore } from '../store/modelsStore';
import { toast } from '../store/uiStore';
import { cn, formatBytes, formatRelativeTime } from '../lib/utils';
import { ApiError } from '../services/apiClient';
import type { KnowledgeBase, KbDocument } from '../types';
import { AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions } from '../lib/permissions';

export function KnowledgeBasesPage() {
  const { has } = useAgentStudioPermissions();
  const canManage = has(P.knowledgeBasesManage);
  const canDelete = has(P.knowledgeBasesDelete);
  const [kbs, setKbs] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<{ name: string; embeddingModel: string } | null>(null);
  const [toDelete, setToDelete] = useState<KnowledgeBase | null>(null);
  const [manage, setManage] = useState<KnowledgeBase | null>(null);
  const embeddings = useModelsStore((s) => s.embeddings);
  const labelOf = useModelsStore((s) => s.labelOf);

  const load = useCallback(async () => {
    setKbs(await knowledgeBasesService.list());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function createKb() {
    if (!form?.name) return;
    try {
      await knowledgeBasesService.save({ name: form.name, embeddingModel: form.embeddingModel });
      setForm(null);
      toast.success('Base criada.');
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Erro ao criar base.');
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;
    try {
      await knowledgeBasesService.remove(toDelete.id);
      toast.success('Base excluída.');
      load();
    } catch {
      toast.error('Erro ao excluir base.');
    }
  }

  const defaultEmbedding = embeddings[0]?.id || 'text-embedding-3-small';

  return (
    <>
      <PageHeader
        title="Bases de Conhecimento"
        subtitle="Repositórios de documentos indexados (RAG) reutilizáveis por agentes e fluxos."
        actions={canManage ? (
          <Button variant="primary" icon={Plus} onClick={() => setForm({ name: '', embeddingModel: defaultEmbedding })}>
            Nova base
          </Button>
        ) : undefined}
      />

      <div className="flex-1 overflow-y-auto scrollbar-thin p-8">
        {loading ? (
          <CardSkeletonGrid count={3} />
        ) : kbs.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="Nenhuma base ainda"
            description="Crie uma base, escolha o modelo de embedding e suba documentos."
            action={canManage ? <Button variant="primary" icon={Plus} onClick={() => setForm({ name: '', embeddingModel: defaultEmbedding })}>Nova base</Button> : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {kbs.map((kb) => (
              <Card key={kb.id} className="flex flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/10 text-navy">
                      <Database className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-ink">{kb.name}</h3>
                      <p className="text-xs text-slate">{kb.documentCount} doc(s) indexado(s)</p>
                    </div>
                  </div>
                  {canDelete && (
                    <button onClick={() => setToDelete(kb)} className="rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Excluir base">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mt-4">
                  <Badge tone="neutral">{labelOf(kb.embeddingModel || 'text-embedding-3-small')}</Badge>
                </div>
                {canManage && (
                  <Button variant="secondary" size="sm" icon={FileText} className="mt-4 self-start" onClick={() => setManage(kb)}>
                    Gerenciar documentos
                  </Button>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Criar base */}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title="Nova base de conhecimento"
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
            <Button variant="primary" onClick={createKb} disabled={!form?.name}>Criar</Button>
          </>
        }
      >
        {form && (
          <>
            <Field label="Nome da base">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex.: Políticas comerciais" />
            </Field>
            <Field label="Modelo de embedding" hint="define a dimensão e a busca vetorial" className="mb-0">
              <Select value={form.embeddingModel} onChange={(e) => setForm({ ...form, embeddingModel: e.target.value })}>
                {embeddings.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </Select>
            </Field>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Excluir base"
        message={`Excluir "${toDelete?.name}" e todos os seus documentos/vetores? Esta ação não pode ser desfeita.`}
      />

      {manage && <DocumentsDrawer kb={manage} onClose={() => { setManage(null); load(); }} />}
    </>
  );
}

// Drawer de gestão de documentos de uma base.
function DocumentsDrawer({ kb, onClose }: { kb: KnowledgeBase; onClose: () => void }) {
  const { has } = useAgentStudioPermissions();
  const canDelete = has(P.knowledgeBasesDelete);
  const [docs, setDocs] = useState<KbDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      setDocs(await knowledgeBasesService.listDocs(kb.id));
    } catch { /* silencioso */ }
  }, [kb.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const processing = docs.some((d) => d.indexStatus === 'pending' || d.indexStatus === 'indexing');
    if (!processing) return;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [docs, refresh]);

  async function addFiles(fileList: FileList | null) {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      setDocs(await knowledgeBasesService.uploadDocs(kb.id, Array.from(fileList)));
      toast.success(`${fileList.length} documento(s) enviado(s).`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  }

  async function removeDoc(id: string) {
    setDocs((d) => d.filter((x) => x.id !== id));
    try {
      await knowledgeBasesService.removeDoc(kb.id, id);
    } catch {
      toast.error('Falha ao remover.');
      refresh();
    }
  }

  return (
    <Drawer open onClose={onClose} title={<span className="flex items-center gap-2"><Database className="h-4 w-4 text-brand-orange" /> {kb.name}</span>}>
      <div className="space-y-4 p-4">
        <div
          className={cn('cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors', dragging ? 'border-brand-orange bg-brand-orange/5' : 'border-line', uploading && 'pointer-events-none opacity-60')}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        >
          <UploadCloud className="mx-auto mb-2 h-6 w-6 text-brand-orange" />
          <p className="text-sm font-medium text-ink">{uploading ? 'Enviando…' : 'Subir documentos'}</p>
          <p className="mt-0.5 text-xs text-slate">PDF, DOCX, CSV, TXT</p>
          <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.doc,.csv,.txt,.md,.json" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate">{docs.length} documento(s)</span>
          <button onClick={refresh} className="flex items-center gap-1 text-xs text-slate hover:text-ink"><RefreshCw className="h-3.5 w-3.5" /> Atualizar</button>
        </div>

        {docs.length === 0 ? (
          <p className="rounded-lg bg-cloud px-3 py-6 text-center text-xs text-slate">Nenhum documento ainda.</p>
        ) : (
          <div className="divide-y divide-line rounded-xl border border-line">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 px-3 py-2.5">
                <FileText className="h-4 w-4 shrink-0 text-slate" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                  <p className="text-xs text-slate">
                    {formatBytes(d.sizeBytes)} · {formatRelativeTime(d.uploadedAt)}
                    {d.indexStatus === 'ready' && d.chunkCount ? ` · ${d.chunkCount} chunks` : ''}
                    {d.indexStatus === 'error' && d.error ? ` · ${d.error}` : ''}
                  </p>
                </div>
                <IndexStatusBadge status={d.indexStatus} />
                {canDelete && (
                  <button onClick={() => removeDoc(d.id)} className="rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger" aria-label="Remover"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
}
